import React, { useState, useEffect } from "react";
import { FiPlus, FiSearch, FiEdit, FiTrash2, FiUpload } from "react-icons/fi";
import { FaFilter } from "react-icons/fa";
import {
  sportsService,
  SportsLegalDocs,
  Docs,
} from "../services/sportsService";

import MainHeader from "../components/MainHeader";
import ConfirmationModal from "../common/ConfirmationModal";
import { motion } from "framer-motion";
import { isStockTradingMode } from "../config";

interface SportsLegalDocsProps {
  onBackToDashboard: () => void;
}

//  renamed to SportsLegalDocsPage
const SportsLegalDocsPage: React.FC<SportsLegalDocsProps> = ({
  onBackToDashboard,
}) => {
  const [docs, setDocs] = useState<SportsLegalDocs[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDoc, setEditingDoc] = useState<SportsLegalDocs | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [docToDelete, setDocToDelete] = useState<SportsLegalDocs | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingDoc, setViewingDoc] = useState<any | null>(null);
  const [expandedDocs, setExpandedDocs] = useState<Record<string, boolean>>({});
  const [termsRows, setTermsRows] = useState<
    Array<{ question: string; answer: string }>
  >([]);

  // Form states for creating/updating documents
  const [formData, setFormData] = useState({
    type: "",
    appType: isStockTradingMode ? "stock" : "crypto",
    content: [{ question: "", answer: "" }],
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingDocId, setEditingDocId] = useState<string | null>(null);

  const fetchDocs = async () => {
    setLoading(true);
    try {
      const response: any = await sportsService.getSportsLegalDocs(
        currentPage,
        10,
        searchTerm
      );
      console.log("===========", response);
      setDocs(response.documents);
      setTotalPages(response.total_pages);
      setTotalDocs(response.total);
    } catch (error) {
      console.error("Error fetching legal docs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocs();
  }, [currentPage, searchTerm, statusFilter]);

  const handleCreateDocs = async () => {
    try {
      // Filter out empty Q&A pairs
      const filteredContent = formData.content.filter(
        (item) => item.question.trim() !== "" && item.answer.trim() !== ""
      );

      if (filteredContent.length === 0) {
        alert("Please add at least one question and answer pair.");
        return;
      }

      const docData = {
        type: formData.type,
        appType: isStockTradingMode ? "stock" : "crypto",
        content: filteredContent,
      };

      if (isEditMode && editingDocId) {
        await sportsService.updateDocs(editingDocId, docData);
      } else {
        await sportsService.createDocs(docData);
      }

      setShowCreateModal(false);
      resetForm();
      fetchDocs();
    } catch (error) {
      console.error("Error creating/updating doc:", error);
    }
  };

  const resetForm = () => {
    setFormData({
      type: "terms",
      appType: isStockTradingMode ? "stock" : "crypto",
      content: [{ question: "", answer: "" }],
    });
    setIsEditMode(false);
    setEditingDocId(null);
  };

  const openEditModal = (doc: any) => {
    setEditingDoc(doc);
    setEditingDocId(doc.id);
    setIsEditMode(true);

    // Pre-populate form with existing data
    setFormData({
      type: doc.type || "terms",
      appType: doc.appType || (isStockTradingMode ? "stock" : "crypto"),
      content:
        doc.content && Array.isArray(doc.content)
          ? doc.content.map((item: any) => ({
              question: item.question || "",
              answer: item.answer || "",
            }))
          : [{ question: "", answer: "" }],
    });

    setShowCreateModal(true);
  };

  const addQAPair = () => {
    setFormData((prev) => ({
      ...prev,
      content: [...prev.content, { question: "", answer: "" }],
    }));
  };

  const removeQAPair = (index: number) => {
    if (formData.content.length > 1) {
      setFormData((prev) => ({
        ...prev,
        content: prev.content.filter((_, i) => i !== index),
      }));
    }
  };

  const updateQAPair = (
    index: number,
    field: "question" | "answer",
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      content: prev.content.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const handleUpdateDocs = async (docId: string, docData: Partial<Docs>) => {
    try {
      await sportsService.updateDocs(docId, docData);
      fetchDocs();
    } catch (error) {
      console.error("Error updating doc:", error);
    }
  };

  const handleDeleteDocs = (doc: SportsLegalDocs) => {
    setDocToDelete(doc);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!docToDelete) return;
    try {
      await sportsService.deleteDocs(docToDelete.id);
      setShowDeleteModal(false);
      setDocToDelete(null);
      fetchDocs();
    } catch (error) {
      console.error("Error deleting doc:", error);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDocToDelete(null);
  };

  const openViewModal = (doc: any) => {
    setViewingDoc(doc);
    // Parse terms content from the document
    let parsed: any = null;
    if (doc && doc.type === "terms" && Array.isArray(doc.content)) {
      parsed = doc;
    }

    if (parsed && Array.isArray(parsed.content)) {
      const rows = parsed.content
        .filter(
          (it: any) =>
            it &&
            typeof it.question === "string" &&
            typeof it.answer === "string"
        )
        .map((it: any) => ({ question: it.question, answer: it.answer }));
      setTermsRows(rows);
    } else {
      setTermsRows([]);
    }
    setShowViewModal(true);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setViewingDoc(null);
    setTermsRows([]);
  };
  const toggleExpand = (docId: string) => {
    setExpandedDocs((prev) => ({
      ...prev,
      [docId]: !prev[docId],
    }));
  };
  // Helper function to render terms preview
  const renderTermsPreview = (doc: any) => {
    const isExpanded = expandedDocs[doc.id] || false;
  
    if (Array.isArray(doc.content)) {
      const previewItems = isExpanded ? doc.content : doc.content.slice(0, 2);
      const remaining = doc.content.length - 2;
  
      return (
        <div className="text-sm space-y-2">
          {previewItems.map((item: any, index: number) => (
            <div key={index} className="border-l-2 border-blue-200 pl-3">
              <div className="text-gray-900 font-medium text-xs mb-1">
                Q: {item.question}
              </div>
              <div className="text-gray-600 text-xs leading-relaxed">
                A: {item.answer}
              </div>
            </div>
          ))}
  
          {remaining > 0 && (
            <button
              type="button"
              onClick={() => toggleExpand(doc.id)}
              className="text-blue-500 text-xs font-medium mt-2"
            >
              {isExpanded ? "Hide extra Q&A" : `+${remaining} more Q&A pairs`}
            </button>
          )}
        </div>
      );
    }
    return <span className="text-gray-500 text-sm">No content</span>;
  };
  
console.log(formData,"==========formData==========")
  return (
    <div>
      <MainHeader onBackToDashboard={onBackToDashboard} />
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6 ">
          <div>
            <motion.button
              onClick={onBackToDashboard}
              className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors duration-200"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Back to Dashboard
            </motion.button>
          </div>
        </div>
        {/* Header end  */}
        <div className="flex items-center justify-between mb-6 ">
          <div>
            <motion.h1
              className="text-3xl font-bold text-gray-900"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              Help & Support
            </motion.h1>
            <motion.p className="mt-2 text-sm text-gray-600">
              Oversee and optimize the app’s Help & Support functions to enhance
              user experience.
            </motion.p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <FiPlus />
              Add Document
            </button>
          </div>
        </div>

        {/* Docs Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">Loading...</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-center">Type</th>
                  <th className="px-6 py-3 text-center">Terms Preview</th>
                  <th className="px-6 py-3 text-center">Updated At</th>
                  {/* <th className="px-6  text-centerpy-3">Status</th> */}
                  <th className="px-6 py-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {docs?.map((doc: any) => (
                  <tr key={doc.id}>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {doc.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 max-w-md">
                      {renderTermsPreview(doc)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(doc.updatedAt).toLocaleDateString()}
                    </td>
                    {/* <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          doc.is_active
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {doc.is_active ? "Active" : "Inactive"}
                      </span>
                    </td> */}
                    <td className="px-6 py-4 flex gap-2">
                      <button
                        onClick={() => openEditModal(doc)}
                        className="text-gray-600 hover:text-gray-800"
                        title="Edit document"
                      >
                        <FiEdit />
                      </button>
                      <button
                        onClick={() => handleDeleteDocs(doc)}
                        className="text-red-600 hover:text-red-800"
                        title="Delete document"
                      >
                        <FiTrash2 />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Create Document Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">
                  {isEditMode ? "Edit Document" : "Add New Document"}
                </h3>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="text-gray-500 hover:text-gray-700 text-xl"
                >
                  ✕
                </button>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleCreateDocs();
                }}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* Title Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title Name
                    </label>
                    {/* <select
                      value={formData.type}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          type: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="terms">Terms</option>
                      <option value="privacy">Privacy Policy</option>
                      <option value="legal">Legal Notice</option>
                      <option value="faq">FAQ</option>
                    </select> */}
                    <input
                      type="text"
                      id="type"
                      name="type"
                      value={formData.type}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          type: e.target.value,
                        }))
                      }
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Wallets & Payment"
                    />
                  </div>
                </div>

                {/* Q&A Pairs */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Questions & Answers
                    </label>
                    <button
                      type="button"
                      onClick={addQAPair}
                      className="flex items-center gap-2 px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                    >
                      <FiPlus className="w-4 h-4" />
                      Add Q&A
                    </button>
                  </div>

                  <div className="space-y-4">
                    {formData.content.map((item, index) => (
                      <div
                        key={index}
                        className="border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium text-gray-700">
                            Q&A Pair #{index + 1}
                          </span>
                          {formData.content.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeQAPair(index)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <FiTrash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Question
                            </label>
                            <input
                              type="text"
                              value={item.question}
                              onChange={(e) =>
                                updateQAPair(index, "question", e.target.value)
                              }
                              placeholder="Enter your question..."
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                              required
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Answer
                            </label>
                            <textarea
                              value={item.answer}
                              onChange={(e) =>
                                updateQAPair(index, "answer", e.target.value)
                              }
                              placeholder="Enter your answer..."
                              rows={3}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                              required
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    {isEditMode ? "Update Document" : "Create Document"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* View Modal */}
        {showViewModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full p-6 max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Document Preview - {viewingDoc?.appType} ({viewingDoc?.type})
                </h3>
                <button
                  onClick={closeViewModal}
                  className="text-gray-500 hover:text-gray-700 text-xl"
                >
                  ✕
                </button>
              </div>
              {termsRows.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Question
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Answer
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {termsRows.map((row, idx) => (
                        <tr key={idx}>
                          <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                            {row.question}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700 whitespace-pre-line">
                            {row.answer}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-sm text-gray-600 text-center py-8">
                  No terms content found in this document.
                </div>
              )}
              <div className="mt-6 flex justify-end">
                <button
                  onClick={closeViewModal}
                  className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteModal}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
          title="Delete Document"
          message={`Are you sure you want to delete this document? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonColor="bg-red-600 hover:bg-red-700"
        />
      </div>
    </div>
  );
};

export default SportsLegalDocsPage;
